import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Card,
  DatePicker,
  Select,
  InputNumber,
  message,
  Space,
  Row,
  Col,
  Spin,
  Alert,
} from 'antd';
import {
  SaveOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import apiService from '../../services/api';
import dayjs from 'dayjs';

const { Option } = Select;

const PaymentEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [vans, setVans] = useState([]);

  const calculateVAT = (netAmount, vatRate) => {
    return (netAmount * vatRate) / 100;
  };

  const calculateTotal = (netAmount, vatAmount) => {
    return netAmount + vatAmount;
  };

  const handleAmountChange = (value) => {
    // Only calculate if value exists and is not 0
    if (value && value > 0) {
      const vatRate = form.getFieldValue('vatRate');
      // If VAT rate is 0 or not provided, set VAT amount to 0
      if (!vatRate || vatRate === 0) {
        form.setFieldsValue({
          vatAmount: 0,
          totalAmount: value
        });
        return;
      }
      
      // Only calculate if we have a valid VAT rate
      if (vatRate !== undefined && vatRate !== null) {
        const vatAmount = calculateVAT(value, vatRate);
        const totalAmount = calculateTotal(value, vatAmount);
        
        form.setFieldsValue({
          vatAmount,
          totalAmount
        });
      }
    } else {
      // Reset VAT and total amounts if amount is 0 or cleared
      form.setFieldsValue({
        vatAmount: 0,
        totalAmount: 0
      });
    }
  };

  const handleVatRateChange = (value) => {
    const amount = form.getFieldValue('amount');
    // If VAT rate is 0 or not provided, set VAT amount to 0
    if (!value || value === 0) {
      form.setFieldsValue({
        vatAmount: 0,
        totalAmount: amount || 0
      });
      return;
    }
    
    // Only calculate if amount exists and is not 0
    if (amount && amount > 0) {
      const vatAmount = calculateVAT(amount, value);
      const totalAmount = calculateTotal(amount, vatAmount);
      
      form.setFieldsValue({
        vatAmount,
        totalAmount
      });
    } else {
      // Reset VAT and total amounts if no amount is entered
      form.setFieldsValue({
        vatAmount: 0,
        totalAmount: 0
      });
    }
  };

  const fetchPayment = useCallback(async () => {
    try {
      setFetching(true);
      setError(null);
      const response = await apiService.payments.getById(id);
      console.log('Payment response:', response);

      const payment = response.data;
      if (!payment) {
        throw new Error('Payment not found');
      }

      form.setFieldsValue({
        ...payment,
        dueDate: payment.dueDate ? dayjs(payment.dueDate) : null,
        totalAmount: Number(payment.totalAmount),
        relatedDriver: payment.relatedDriver?._id || payment.relatedDriver || undefined,
        relatedVan: payment.relatedVan?._id || payment.relatedVan || undefined,
      });
    } catch (error) {
      console.error('Error fetching payment:', error);
      setError(error.response?.data?.message || error.message || 'Failed to fetch payment details');
      message.error('Failed to fetch payment details');
    } finally {
      setFetching(false);
    }
  }, [id, form]);

  useEffect(() => {
    fetchPayment();
    fetchDriversAndVans();
  }, [fetchPayment]);

  const fetchDriversAndVans = async () => {
    try {
      const [driversRes, vansRes] = await Promise.all([
        apiService.drivers.getAll(),
        apiService.vans.getAll()
      ]);
      setDrivers(driversRes.data);
      setVans(vansRes.data);
    } catch (error) {
      console.error('Error fetching drivers or vans:', error);
    }
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);
      const formattedValues = {
        ...values,
        dueDate: values.dueDate?.toISOString(),
        totalAmount: Number(values.totalAmount),
        vatAmount: Number(values.vatAmount),
        amount: Number(values.amount),
        vatRate: Number(values.vatRate)
      };
      
      const response = await apiService.payments.update(id, formattedValues);
      
      if (response.data) {
        message.success('Payment updated successfully');
        navigate('/payments');
      } else {
        throw new Error('Failed to update payment');
      }
    } catch (error) {
      console.error('Error updating payment:', error);
      message.error(error.response?.data?.message || error.message || 'Failed to update payment');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Card
        title="Edit Payment"
        extra={
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/payments')}
          >
            Back to Payments
          </Button>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
        >
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <Form.Item
                name="reference"
                label="Reference"
                rules={[{ required: true, message: 'Reference is required' }]}
              >
                <Input disabled />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="category"
                label="Category"
                rules={[{ required: true, message: 'Please select category' }]}
              >
                <Select>
                  <Option value="fuel">Fuel</Option>
                  <Option value="maintenance">Maintenance</Option>
                  <Option value="insurance">Insurance</Option>
                  <Option value="insurance claim">Insurance Claim</Option>
                  <Option value="salary">Salary</Option>
                  <Option value="van rental">Van Rental</Option>
                  <Option value="van deposits">Van Deposits</Option>
                  <Option value="tracking charges">Tracking Charges</Option>
                  <Option value="office">Office</Option>
                  <Option value="job">Job</Option>
                  <Option value="charges">Charges</Option>
                  <Option value="porter">Porter</Option>
                  <Option value="accounting services">Accounting Services</Option>
                  <Option value="paye">PAYE</Option>
                  <Option value="excess mileage">Excess Mileage</Option>
                  <Option value="vat liability">VAT Liability</Option>
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="plPosition"
                label="PL Position"
                rules={[{ required: true, message: 'Please select PL position' }]}
              >
                <Select>
                  <Option value="income">Income</Option>
                  <Option value="expense">Expense</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="amount"
                label="Net Amount (£)"
                rules={[{ required: true, message: 'Please enter amount' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  precision={2}
                  onChange={handleAmountChange}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="vatRate"
                label="VAT Rate (%)"
                rules={[{ required: true, message: 'Please enter VAT rate' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  max={100}
                  step={0.1}
                  precision={1}
                  onChange={handleVatRateChange}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="vatAmount"
                label="VAT Amount (£)"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  disabled
                  precision={2}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="totalAmount"
                label="Total Amount (£)"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  disabled
                  precision={2}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="dueDate"
                label="Due Date"
                rules={[{ required: true, message: 'Please select due date' }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="Select due date"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="paymentMethod"
                label="Payment Method"
                rules={[{ required: true, message: 'Please select payment method' }]}
              >
                <Select>
                  <Option value="bank_transfer">Bank Transfer</Option>
                  <Option value="credit_card">Credit Card</Option>
                  <Option value="debit_card">Debit Card</Option>
                  <Option value="cash">Cash</Option>
                  <Option value="check">Check</Option>
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select>
                  <Option value="pending">Pending</Option>
                  <Option value="paid">Paid</Option>
                  <Option value="overdue">Overdue</Option>
                  <Option value="cancelled">Cancelled</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="relatedDriver"
                label="Link to Driver"
                tooltip="Link this payment to a specific driver"
              >
                <Select
                  allowClear
                  placeholder="Select driver"
                  showSearch
                  optionFilterProp="children"
                >
                  {drivers.map(driver => (
                    <Option key={driver._id} value={driver._id}>
                      {driver.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="relatedVan"
                label="Link to Vehicle"
                tooltip="Link this payment to a specific vehicle"
              >
                <Select
                  allowClear
                  placeholder="Select vehicle"
                  showSearch
                  optionFilterProp="children"
                >
                  {vans.map(van => (
                    <Option key={van._id} value={van._id}>
                      {van.registration} ({van.make} {van.model})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="receipt"
                label="Receipt URL"
              >
                <Input placeholder="Enter URL to receipt image/document" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="notes"
                label="Notes"
              >
                <Input.TextArea rows={3} placeholder="Enter any additional notes about this payment" />
              </Form.Item>
            </Col>
            <Col span={24} style={{ textAlign: 'right', marginTop: 16 }}>
              <Space>
                <Button onClick={() => navigate('/payments')}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<SaveOutlined />}
                >
                  Save Changes
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>
    </div>
  );
};

export default PaymentEdit; 